import { Button } from '#app/catalyst-ui/button.js'
import { ErrorMessage, Field, FieldGroup, Label } from '#app/catalyst-ui/fieldset.js'
import { Input } from '#app/catalyst-ui/input.js'
import { Textarea } from '#app/catalyst-ui/textarea.js'
import type { Handle } from '#app/root.js'
import { sendInternalEmail } from '#app/utils/email.server'
import { isRateLimited } from '#app/utils/rate-limit.server.js'
import { flashToast } from '#app/utils/toast.server.js'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import type { ActionFunctionArgs, MetaFunction } from '@remix-run/node'
import { data, Form, useActionData, useNavigation } from '@remix-run/react'
import { z } from 'zod'

export const meta: MetaFunction = () => [
	{ title: 'Contact Us - GradeGuide' },
	{
		name: 'description',
		content:
			"Got a question, spotted a bug, or have a cool feature idea? Drop us a message below and we'll get back to you soon as possible.",
	},
]

export const handle: Handle = {
	getSitemapEntries: () => [{ priority: 0.7, changeFreq: 'monthly' }],
}

const contactSchema = z.object({
	name: z.string().min(1),
	email: z.string().email(),
	message: z.string().min(1),
	confirm: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const submission = parseWithZod(await request.formData(), { schema: contactSchema })

	const { limited, headers } = await isRateLimited({ request, limit: 3, period: 60 })
	if (limited) return data({ submission: submission.reply() }, { status: 429, headers })

	if (submission.status !== 'success') return data({ submission: submission.reply() }, { status: 400 })

	const { name, email, message, confirm } = submission.value

	// Silently accept bot submissions without sending email
	if (confirm) {
		const { headers: successHeaders } = await flashToast({
			type: 'success',
			message: 'Message sent',
		})
		return data({ submission: submission.reply({ resetForm: true }) }, { headers: successHeaders })
	}

	await sendInternalEmail({
		from: email,
		subject: `Message from ${name} - (${email})`,
		react: <ContactEmail message={message} />,
	})

	const { headers: successHeaders } = await flashToast({
		type: 'success',
		message: 'Message sent',
	})

	return data({ submission: submission.reply({ resetForm: true }) }, { headers: successHeaders })
}

export default function Index() {
	const navigation = useNavigation()

	const actionData = useActionData<typeof action>()
	const [form, fields] = useForm({
		lastResult: actionData?.submission,
		onValidate: ({ formData }) => parseWithZod(formData, { schema: contactSchema }),
		constraint: getZodConstraint(contactSchema),
	})

	return (
		<>
			<h1>Contact us</h1>
			<p>
				Got a question, spotted a bug, or have a cool feature idea? Drop us a message below and we'll get back to you
				soon!
			</p>

			<Form method="POST" className="mt-12 flex grow flex-col" {...getFormProps(form)}>
				<FieldGroup>
					<Field>
						<Label htmlFor={fields.name.id}>Name</Label>
						<Input {...getInputProps(fields.name, { type: 'text' })} autoFocus={!fields.name.dirty} />
						{fields.name.errors ? <ErrorMessage>{fields.name.errors[0]}</ErrorMessage> : null}
					</Field>

					<Field>
						<Label htmlFor={fields.email.id}>Email</Label>
						<Input {...getInputProps(fields.email, { type: 'email' })} />
						{fields.email.errors ? <ErrorMessage>{fields.email.errors[0]}</ErrorMessage> : null}
					</Field>

					<Field>
						<Label htmlFor={fields.message.id}>Message</Label>
						<Textarea {...getInputProps(fields.message, { type: 'text' })} />
						{fields.message.errors ? <ErrorMessage>{fields.message.errors[0]}</ErrorMessage> : null}
					</Field>

					<input
						{...getInputProps(fields.confirm, { type: 'text' })}
						aria-label="Please leave this field empty"
						className="hidden"
						autoComplete="off"
						tabIndex={-1}
					/>
				</FieldGroup>

				<div className="mt-auto flex items-center justify-end gap-3 pt-8 *:w-full sm:mt-0 sm:flex-row sm:*:w-auto">
					<Button type="submit" disabled={navigation.state !== 'idle'} data-umami-event="contact.send_button.clicked">
						Send message
					</Button>
				</div>
			</Form>
		</>
	)
}

function ContactEmail({ message }: { message: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Text>{message}</E.Text>
		</E.Html>
	)
}
